import { lazy, Suspense } from 'react'
import { ExternalLink, ChevronDown, ChevronUp, Clock, MapPin } from 'lucide-react'
import type { StationWithDistance, FuelType } from '@fuelfinder/shared'

// Lazy load the mini map to avoid loading Leaflet until needed
const MiniMap = lazy(() => import('./MiniMap').then((m) => ({ default: m.MiniMap })))

interface StationCardProps {
  station: StationWithDistance
  fuelType: 'E10' | 'B7'
  expanded?: boolean
  onToggle?: () => void
  pricePercentile: number // 0-100
  savingsEstimate?: string | null // e.g., "Save £8"
}

/**
 * Get Tailwind text color class based on price percentile
 * Green for cheapest 25%, amber for middle 50%, red for top 25%
 */
function getPriceColorClass(percentile: number): string {
  if (percentile <= 25) return 'text-green-600'
  if (percentile <= 75) return 'text-amber-500'
  return 'text-red-500'
}

/**
 * Get Tailwind border color class based on price percentile
 */
function getPriceBorderClass(percentile: number): string {
  if (percentile <= 25) return 'border-green-500'
  if (percentile <= 75) return 'border-amber-400'
  return 'border-red-400'
}

/**
 * Format price in pence to display format (e.g., 131.9p)
 */
function formatPrice(pricePence: number | null): string {
  if (pricePence === null) return 'N/A'
  return `${pricePence.toFixed(1)}p`
}

/**
 * Format distance in miles (e.g., "0.3 mi")
 */
function formatDistance(miles: number): string {
  if (miles < 0.1) return '< 0.1 mi'
  return `${miles.toFixed(1)} mi`
}

/**
 * Get Google Maps directions URL
 */
function getDirectionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`
}

/**
 * Format date string to human-readable relative time
 */
function formatRelativeTime(isoString: string): string {
  try {
    const date = new Date(isoString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
    })
  } catch {
    return isoString
  }
}

/**
 * Fuel type labels for display
 */
const FUEL_LABELS: Record<FuelType, string> = {
  E10: 'Petrol',
  B7: 'Diesel',
  E5: 'Super',
  SDV: 'Premium Diesel',
}

/**
 * Loading placeholder for mini map
 */
function MiniMapLoading() {
  return (
    <div className="w-full h-40 bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
        <MapPin size={24} className="animate-pulse" />
        <span className="text-sm">Loading map...</span>
      </div>
    </div>
  )
}

export function StationCard({
  station,
  fuelType,
  expanded = false,
  onToggle,
  pricePercentile,
  savingsEstimate,
}: StationCardProps) {
  const price = station.prices[fuelType]
  const priceColorClass = getPriceColorClass(pricePercentile)
  const priceBorderClass = getPriceBorderClass(pricePercentile)

  return (
    <div
      className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border-2 ${priceBorderClass} overflow-hidden`}
      onClick={onToggle}
      role={onToggle ? 'button' : undefined}
      tabIndex={onToggle ? 0 : undefined}
      onKeyDown={
        onToggle
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onToggle()
              }
            }
          : undefined
      }
    >
      {/* Main card content */}
      <div className="p-4 cursor-pointer">
        <div className="flex items-start justify-between gap-3">
          {/* Left: Brand, address, distance */}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white truncate">
              {station.brand}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
              {station.address}
            </p>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-sm text-slate-600 dark:text-slate-300">
                {formatDistance(station.distanceMiles)}
              </span>
              <span className="flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500">
                <Clock size={12} />
                Updated {formatRelativeTime(station.updatedAt)}
              </span>
            </div>
          </div>

          {/* Right: Price and expand indicator */}
          <div className="flex flex-col items-end">
            <span className={`text-3xl font-bold ${priceColorClass}`}>
              {formatPrice(price)}
            </span>
            {savingsEstimate && (
              <span className={`text-sm ${priceColorClass} font-medium mt-1`}>
                {savingsEstimate}
              </span>
            )}
            {onToggle && (
              <span className="text-slate-400 mt-2">
                {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
          {/* Lazy-loaded mini map with same style as main map */}
          <Suspense fallback={<MiniMapLoading />}>
            <MiniMap lat={station.lat} lng={station.lng} brand={station.brand} />
          </Suspense>

          <div className="px-4 py-4">
            {/* All fuel prices */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-3">
                All Prices
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {(['E10', 'B7', 'E5', 'SDV'] as FuelType[]).map((ft) => (
                  <div key={ft} className="flex justify-between text-base">
                    <span className="text-slate-600 dark:text-slate-300">
                      {FUEL_LABELS[ft]}
                    </span>
                    <span
                      className={`font-semibold ${
                        ft === fuelType
                          ? priceColorClass
                          : 'text-slate-900 dark:text-white'
                      }`}
                    >
                      {formatPrice(station.prices[ft])}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Full address */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-2">
                Address
              </h4>
              <p className="text-base text-slate-700 dark:text-slate-200">
                {station.address}
              </p>
              <p className="text-base text-slate-700 dark:text-slate-200">
                {station.postcode}
              </p>
            </div>

            {/* Navigate button */}
            <a
              href={getDirectionsUrl(station.lat, station.lng)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="flex items-center justify-center gap-2 w-full py-4 bg-green-600 hover:bg-green-700 text-white text-lg font-semibold rounded-lg transition-colors"
            >
              <ExternalLink size={20} />
              Navigate
            </a>
          </div>
        </div>
      )}
    </div>
  )
}
